import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Stack,
  IconButton,
  Grow,
  alpha,
  Alert,
  Collapse,
  Tooltip,
  Fade,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Mic, Square, Wand2, Copy, Check, X, Mic2 } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { config } from '../../../config';
import {
  MediaRecorderState,
  ProgressModal,
  FeedbackSnackbar,
  stopMediaRecording,
  GEMINI_MODEL,
} from './common';

interface SmartNotePadState extends MediaRecorderState {
  transcription: string;
  notes: string;
  enhancedNotes: string;
  copySuccess: boolean;
  showRecordingPrompt: boolean;
}

const SmartNotePad: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<SmartNotePadState>({
    isRecording: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
    transcription: '',
    notes: '',
    enhancedNotes: '',
    copySuccess: false,
    showRecordingPrompt: false,
  });

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const recognition = useRef<any>(null);
  const audioStream = useRef<MediaStream | null>(null);

  // Add a timeout ref for the recording prompt
  const promptTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Initialize speech recognition
    if (!recognition.current) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognition.current = new SpeechRecognition();
        recognition.current.continuous = true;
        recognition.current.interimResults = true;

        recognition.current.onresult = (event: any) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = 0; i < event.results.length; i += 1) {
            if (event.results[i].isFinal) {
              finalTranscript += event.results[i][0].transcript;
            } else {
              interimTranscript += event.results[i][0].transcript;
            }
          }

          setState((prev) => ({
            ...prev,
            transcription:
              prev.transcription + finalTranscript + interimTranscript,
          }));
        };

        recognition.current.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setState((prev) => ({
            ...prev,
            error: `Speech recognition error: ${event.error}`,
            isRecording: false,
          }));
        };

        recognition.current.onend = () => {
          setState((prev) => ({ ...prev, isRecording: false }));
        };
      }
    }
  }, []);

  // Handle typing in the notes field
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newNotes = e.target.value;
    setState((prev) => ({ ...prev, notes: newNotes }));

    // Show recording prompt if not recording and user starts typing
    if (!state.isRecording && newNotes && !state.showRecordingPrompt) {
      setState((prev) => ({ ...prev, showRecordingPrompt: true }));

      // Hide the prompt after 10 seconds if user doesn't take action
      if (promptTimeout.current) {
        clearTimeout(promptTimeout.current);
      }
      promptTimeout.current = setTimeout(() => {
        setState((prev) => ({ ...prev, showRecordingPrompt: false }));
      }, 10000);
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (promptTimeout.current) {
        clearTimeout(promptTimeout.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      setState((prev) => ({
        ...prev,
        error: null,
        transcription: '',
        showRecordingPrompt: false,
      }));

      // Check if browser supports required APIs
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Browser does not support media devices');
      }

      if (!recognition.current) {
        throw new Error('Speech recognition not supported');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      audioStream.current = stream;

      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000,
      };

      mediaRecorder.current = MediaRecorder.isTypeSupported(options.mimeType)
        ? new MediaRecorder(stream, options)
        : new MediaRecorder(stream);

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      mediaRecorder.current.start(1000);
      recognition.current.start();
      setState((prev) => ({ ...prev, isRecording: true }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: t('smartNotePad.messages.micError'),
        showRecordingPrompt: false,
      }));
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && state.isRecording) {
      stopMediaRecording(mediaRecorder, [audioStream.current]);
      recognition.current?.stop();
    }
  };

  const enhanceNotes = async () => {
    try {
      setState((prev) => ({ ...prev, uploading: true }));

      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: GEMINI_MODEL,
          contents: [
            {
              parts: [
                {
                  text: `You are a note enhancement AI. Combine these notes with the transcription to create clear, comprehensive notes. Keep the style simple and direct.

Transcription of the recording:
${state.transcription}

Original notes:
${state.notes}

Rules:
1. Start with the original notes in **bold**
2. Add any important details from the transcription underneath
3. Keep the format clean and simple
4. Use bullet points for new information
5. Don't add any headers or sections - just the enhanced notes

Example format:
**[Original notes go here in bold]**
• Additional point from transcription
• Another important detail
• Key insight mentioned in recording

Remember: Keep it simple and straightforward - just the enhanced notes.`,
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to enhance notes');
      }

      const data = await response.json();
      const enhancedNotes = data.candidates[0].content.parts[0].text;

      setState((prev) => ({
        ...prev,
        enhancedNotes,
        uploading: false,
        uploadComplete: true,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: t('smartNotePad.messages.enhanceError'),
        uploading: false,
      }));
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setState((prev) => ({ ...prev, copySuccess: true }));
      setTimeout(() => {
        setState((prev) => ({ ...prev, copySuccess: false }));
      }, 2000);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: t('smartNotePad.messages.copyError'),
      }));
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
        minHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header Section - Recording Button Only */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 4,
        }}
      >
        <Tooltip
          title={
            state.isRecording
              ? t('smartNotePad.tooltips.stop')
              : t('smartNotePad.tooltips.record')
          }
          placement="bottom"
          TransitionComponent={Fade}
          arrow
        >
          <Button
            variant="contained"
            color={state.isRecording ? 'error' : 'primary'}
            onClick={state.isRecording ? stopRecording : startRecording}
            startIcon={state.isRecording ? <Square /> : <Mic />}
            size="large"
            sx={{
              borderRadius: 28,
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              transition: 'all 0.3s ease',
              minWidth: 250,
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          >
            {state.isRecording
              ? t('smartNotePad.controls.stop')
              : t('smartNotePad.controls.start')}
          </Button>
        </Tooltip>
      </Box>

      {/* Product Explanation */}
      <Alert
        severity="info"
        sx={{
          mb: 3,
          borderRadius: 2,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
        >
          {t('smartNotePad.description')}
        </Typography>
      </Alert>

      {/* Recording Status */}
      <Fade in={state.isRecording}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            px: 2,
          }}
        >
          <Mic2
            color="error"
            sx={{
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              },
            }}
          />
          <Typography
            variant="body2"
            color="error"
            sx={{ fontStyle: 'italic' }}
          >
            {t('smartNotePad.status.recording')}
          </Typography>
        </Box>
      </Fade>

      {/* Recording Prompt */}
      <Collapse in={state.showRecordingPrompt}>
        <Alert
          severity="info"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                color="info"
                size="small"
                startIcon={<Mic />}
                onClick={startRecording}
              >
                {t('smartNotePad.controls.start')}
              </Button>
              <IconButton
                size="small"
                onClick={() =>
                  setState((prev) => ({ ...prev, showRecordingPrompt: false }))
                }
              >
                <X />
              </IconButton>
            </Stack>
          }
          sx={{
            mb: 2,
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          {t('smartNotePad.status.prompt')}
        </Alert>
      </Collapse>

      <Stack
        spacing={3}
        sx={{
          flex: 1,
          display: 'flex',
        }}
      >
        {/* Notes Section */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 3,
            bgcolor: (theme: Theme) =>
              alpha(theme.palette.background.paper, 0.7),
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: (theme: Theme) =>
              alpha(theme.palette.primary.main, 0.1),
            position: 'relative',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            '&:hover': {
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              borderColor: (theme: Theme) =>
                alpha(theme.palette.primary.main, 0.2),
              transition: 'all 0.3s ease',
            },
          }}
        >
          <Box sx={{ position: 'relative', height: '100%' }}>
            <TextField
              fullWidth
              multiline
              rows={12}
              value={state.notes}
              onChange={handleNotesChange}
              placeholder={t('smartNotePad.placeholders.notes')}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: '16px',
                  lineHeight: '1.6',
                  p: 2,
                  '& textarea': {
                    lineHeight: '1.6 !important',
                  },
                  '& textarea::placeholder': {
                    opacity: 0.6,
                  },
                },
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                bottom: -16,
                right: 0,
                zIndex: 1,
              }}
            >
              <Tooltip
                title={
                  !state.transcription || !state.notes
                    ? t('smartNotePad.tooltips.enhance')
                    : ''
                }
                placement="top"
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={enhanceNotes}
                    startIcon={<Wand2 />}
                    disabled={!state.transcription || !state.notes}
                    sx={{
                      borderRadius: 28,
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      backdropFilter: 'blur(10px)',
                      backgroundColor: (theme: Theme) =>
                        alpha(theme.palette.primary.main, 0.9),
                      transition: 'all 0.3s ease',
                      '&:not(:disabled):hover': {
                        transform: 'scale(1.05)',
                        backgroundColor: (theme: Theme) =>
                          theme.palette.primary.main,
                      },
                    }}
                  >
                    {t('smartNotePad.controls.enhance')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Paper>

        {/* Enhanced Notes Section */}
        {state.enhancedNotes && (
          <Grow in={!!state.enhancedNotes} timeout={500}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: (theme: Theme) =>
                  alpha(theme.palette.background.paper, 0.7),
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                border: '1px solid',
                borderColor: (theme: Theme) =>
                  alpha(theme.palette.primary.main, 0.1),
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  borderColor: (theme: Theme) =>
                    alpha(theme.palette.primary.main, 0.2),
                  transition: 'all 0.3s ease',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 2,
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: (theme: Theme) =>
                      alpha(theme.palette.text.primary, 0.9),
                  }}
                >
                  {t('smartNotePad.labels.enhancedNotes')}
                </Typography>
                <Tooltip title={t('smartNotePad.controls.copy')} arrow>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(state.enhancedNotes)}
                    sx={{
                      transition: 'all 0.2s ease',
                      backgroundColor: (theme: Theme) =>
                        alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        transform: 'scale(1.1)',
                        backgroundColor: (theme: Theme) =>
                          alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  >
                    {state.copySuccess ? <Check color="success" /> : <Copy />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  p: 2,
                  '& p': { my: 1 },
                  '& strong': {
                    color: (theme: Theme) => theme.palette.primary.main,
                    fontWeight: 600,
                  },
                  '& ul': {
                    pl: 2,
                    my: 1,
                  },
                  '& li': {
                    my: 0.5,
                  },
                }}
              >
                <ReactMarkdown>{state.enhancedNotes}</ReactMarkdown>
              </Box>
            </Paper>
          </Grow>
        )}
      </Stack>

      <ProgressModal
        open={state.uploading}
        progress={state.progress}
        isComplete={state.uploadComplete}
        processingMessage={t('smartNotePad.messages.processing')}
        completeMessage={t('smartNotePad.messages.complete')}
      />

      <FeedbackSnackbar
        open={!!state.error}
        message={state.error || t('smartNotePad.messages.error')}
        severity="error"
        onClose={() => setState((prev) => ({ ...prev, error: null }))}
      />
    </Box>
  );
};

export default SmartNotePad;
